import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Upload, Eye, Home, Building, TreePine, GripVertical, Search, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import MultilingualDescriptionEditor from '../../components/MultilingualDescriptionEditor';
import { useTranslation } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LISTING_TYPES = { sale: 'Продажба', rent: 'Под наем' };
const CATEGORIES = {
  apartment: 'Апартамент', house: 'Къща', villa: 'Вила', 
  land: 'Парцел', office: 'Офис', commercial: 'Търговски обект',
  garage: 'Гараж', studio: 'Студио', project: 'Проект'
};
const STATUSES = { active: 'Активна', draft: 'Чернова', sold: 'Продадено', rented: 'Отдадено' };

export default function AdminListings() {
  const { t } = useTranslation();
  const { user, isBroker } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filters, setFilters] = useState({ search: '', type: 'all', category: 'all', status: 'all' });

  useEffect(() => { fetchListings(); }, []);

  const fetchListings = async () => {
    try {
      const r = await axios.get(`${API}/listings`);
      setListings(r.data);
    } catch { toast.error('Грешка при зареждане'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Сигурни ли сте?')) return;
    try {
      await axios.delete(`${API}/listings/${id}`);
      toast.success('Обявата е изтрита');
      fetchListings();
    } catch { toast.error('Грешка при изтриване'); }
  };

  const handleSave = async (data) => {
    try {
      if (editing) {
        await axios.put(`${API}/listings/${editing.id}`, data);
        toast.success('Обявата е обновена');
      } else {
        await axios.post(`${API}/listings`, data);
        toast.success('Обявата е създадена');
      }
      setShowModal(false);
      setEditing(null);
      fetchListings();
    } catch (e) {
      toast.error('Грешка при запазване');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner"></div></div>;

  const filtered = listings.filter(l => {
    if (filters.type !== 'all' && l.listing_type !== filters.type) return false;
    if (filters.category !== 'all' && l.category !== filters.category) return false;
    if (filters.status !== 'all' && l.status !== filters.status) return false;
    if (filters.search) {
      const q = filters.search.toLowerCase();
      return (
        l.title?.toLowerCase().includes(q) ||
        l.location?.toLowerCase().includes(q) ||
        l.neighborhood?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const hasActiveFilters = filters.search || filters.type !== 'all' || filters.category !== 'all' || filters.status !== 'all';
  const clearFilters = () => setFilters({ search: '', type: 'all', category: 'all', status: 'all' });

  return (
    <div data-testid="admin-listings">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">{t('listings.admin_title')}</h1>
          <p className="text-muted-foreground">{t('listings.admin_subtitle')}</p>
        </div>
        {isBroker && (
          <div className="text-sm text-muted-foreground bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg">
{t('listings.broker_own_only')}
          </div>
        )}
        <Button className="bg-[#1B5E37] hover:bg-[#154a2c]" onClick={() => { setEditing(null); setShowModal(true); }} data-testid="new-listing-btn">
          <Plus className="w-4 h-4 mr-2" />Нова обява
        </Button>
      </div>

      {listings.length === 0 ? (
        <div className="admin-card text-center py-16">
          <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
          <p className="text-lg text-muted-foreground mb-2">{t('listings.no_listings')}</p>
          <p className="text-sm text-muted-foreground mb-6">{t('listings.create_first')}</p>
          <Button onClick={() => { setEditing(null); setShowModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />{t('listings.new_listing')}
          </Button>
        </div>
      ) : (
        <>
          {/* Filter bar */}
          <div className="flex flex-wrap gap-3 mb-4 p-4 bg-white rounded-xl border border-border shadow-sm">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('listings.search')}
                value={filters.search}
                onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                className="pl-10"
                data-testid="admin-listing-search-input"
              />
            </div>
            <Select value={filters.status} onValueChange={(v) => setFilters(f => ({ ...f, status: v }))}>
              <SelectTrigger className="w-40" data-testid="admin-listing-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Всички статуси</SelectItem>
                {Object.entries(STATUSES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.type} onValueChange={(v) => setFilters(f => ({ ...f, type: v }))}>
              <SelectTrigger className="w-40" data-testid="admin-listing-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listings.all')}</SelectItem>
                <SelectItem value="sale">{t('listings.sale')}</SelectItem>
                <SelectItem value="rent">{t('listings.rent')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.category} onValueChange={(v) => setFilters(f => ({ ...f, category: v }))}>
              <SelectTrigger className="w-44" data-testid="admin-listing-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('listings.all_categories')}</SelectItem>
                {Object.entries(CATEGORIES).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-foreground gap-1.5">
                <X className="w-3.5 h-3.5" />
                {t('listings.clear_filters')}
              </Button>
            )}
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            Показани <strong>{filtered.length}</strong> от {listings.length} обяви
          </p>

          {filtered.length === 0 ? (
            <div className="admin-card text-center py-16">
              <Home className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-40" />
              <p className="text-lg text-muted-foreground mb-2">{t('listings.no_results')}</p>
              <Button variant="outline" onClick={clearFilters}>
                <X className="w-4 h-4 mr-2" />{t('listings.clear_filters')}
              </Button>
            </div>
          ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map(listing => (
            <div key={listing.id} className="admin-card overflow-hidden" data-testid={`admin-listing-${listing.id}`}>
              <div className="relative h-40 -mx-6 -mt-6 mb-4">
                <img 
                  src={listing.images?.[0] || 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400'}
                  alt={listing.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 left-2 flex gap-1">
                  <Badge className={listing.listing_type === 'sale' ? 'bg-[#1B5E37] text-white' : 'bg-blue-600 text-white'}>
                    {LISTING_TYPES[listing.listing_type]}
                  </Badge>
                  <Badge variant="secondary">{CATEGORIES[listing.category]}</Badge>
                </div>
                <Badge className={`absolute top-2 right-2 ${listing.status === 'active' ? 'bg-green-100 text-green-800' : listing.status === 'draft' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'}`}>
                  {STATUSES[listing.status]}
                </Badge>
              </div>
              <h3 className="font-semibold text-base mb-1 line-clamp-1">{listing.title}</h3>
              <p className="text-sm text-muted-foreground mb-1 flex items-center gap-1">
                {listing.location}{listing.neighborhood ? `, кв. ${listing.neighborhood}` : ''}
              </p>
              <p className="font-bold text-[#1B5E37] mb-4">
                {new Intl.NumberFormat('bg-BG').format(listing.price)} EUR{listing.listing_type === 'rent' ? t('listings.per_month') : ''}
              </p>
              <div className="flex gap-2">
                {(!isBroker || listing.created_by === user?.id) && (
                <Button variant="outline" size="sm" className="flex-1" onClick={() => { setEditing(listing); setShowModal(true); }}>
                  <Pencil className="w-3 h-3 mr-1" />{t('admin.edit')}
                </Button>
                )}
                {!isBroker && (
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(listing.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
                )}
                <Button variant="outline" size="sm" asChild>
                  <a href={`/listings/${listing.id}`} target="_blank" rel="noopener noreferrer"><Eye className="w-3 h-3" /></a>
                </Button>
              </div>
            </div>
          ))}
        </div>
          )}
        </>
      )}

      <ListingModal 
        open={showModal}
        onClose={() => { setShowModal(false); setEditing(null); }}
        onSave={handleSave}
        data={editing}
      />
    </div>
  );
}


// Reusable markets selector
const MARKET_OPTIONS = [
  { code: 'bg', label: '🇧🇬 България' },
  { code: 'gr', label: '🇬🇷 Гърция' },
  { code: 'tr', label: '🇹🇷 Турция' },
  { code: 'en', label: '🌍 Global (EN)' },
];

function MarketsSelector({ value, onChange }) {
  const markets = value || ['bg', 'gr', 'tr', 'en']; // default: all markets
  return (
    <div>
      <Label className="mb-2 block font-medium">Пазари (Markets)</Label>
      <div className="flex flex-wrap gap-3">
        {MARKET_OPTIONS.map(m => (
          <label key={m.code} className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={markets.includes(m.code)}
              onChange={(e) => {
                if (e.target.checked) {
                  onChange([...new Set([...markets, m.code])]);
                } else {
                  onChange(markets.filter(x => x !== m.code));
                }
              }}
              className="w-4 h-4 accent-[#1B5E37]"
            />
            <span className="text-sm">{m.label}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        Избери в кои пазари да се показва. Без избор = вижда се навсякъде.
      </p>
    </div>
  );
}

function ListingModal({ open, onClose, onSave, data }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '', description: '', description_el: '', description_tr: '', description_en: '', listing_type: 'sale', category: 'apartment',
    status: 'active', price: '', area: '', rooms: '', floor: '',
    year_built: '', location: '', neighborhood: '', latitude: '', longitude: '',
    contact_name: '', contact_phone: '', contact_email: '',
    features: [], images: [], markets: ['bg', 'gr', 'tr', 'en']
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) {
      setFormData({
        title: data.title || '', description: data.description || '',
        description_el: data.description_el || '', description_tr: data.description_tr || '', description_en: data.description_en || '',
        listing_type: data.listing_type || 'sale', category: data.category || 'apartment',
        status: data.status || 'active', price: data.price || '',
        area: data.area || '', rooms: data.rooms || '', floor: data.floor || '',
        year_built: data.year_built || '', location: data.location || '',
        neighborhood: data.neighborhood || '',
        latitude: data.latitude || '', longitude: data.longitude || '',
        contact_name: data.contact_name || '', contact_phone: data.contact_phone || '',
        contact_email: data.contact_email || '',
        features: data.features || [], images: data.images || [],
        markets: data.markets || ['bg', 'gr', 'tr', 'en']
      });
    } else {
      setFormData({
        title: '', description: '', description_el: '', description_tr: '', description_en: '', listing_type: 'sale', category: 'apartment',
        status: 'active', price: '', area: '', rooms: '', floor: '',
        year_built: '', location: '', neighborhood: '', latitude: '', longitude: '',
        contact_name: '', contact_phone: '', contact_email: '',
        features: [], images: [], markets: ['bg', 'gr', 'tr', 'en']
      });
    }
  }, [data, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave({
      ...formData,
      price: parseFloat(formData.price) || 0,
      area: parseFloat(formData.area) || 0,
      rooms: parseInt(formData.rooms) || 0,
      latitude: parseFloat(formData.latitude) || null,
      longitude: parseFloat(formData.longitude) || null
    });
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const r = await axios.post(`${API}/upload?category=listing&entity_id=${data?.id || 'new'}`, fd);
        const url = r.data.url?.startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
        setFormData(f => ({ ...f, images: [...f.images, url] }));
      } catch { toast.error('Грешка при качване'); }
    }
    toast.success('Снимките са качени');
  };

  const removeImage = (idx) => {
    setFormData(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{data ? t('listings.edit_listing') : t('listings.new_listing')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label>{t('admin.title')} *</Label>
              <Input value={formData.title} onChange={(e) => setFormData(f => ({ ...f, title: e.target.value }))} required placeholder="Тристаен апартамент в центъра на София" data-testid="listing-title-input" />
            </div>
            <div>
              <Label>{t('listings.type_label')}</Label>
              <Select value={formData.listing_type} onValueChange={(v) => setFormData(f => ({ ...f, listing_type: v }))}>
                <SelectTrigger data-testid="listing-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sale">{t('listings.sale')}</SelectItem>
                  <SelectItem value="rent">{t('listings.rent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('props.category')}</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData(f => ({ ...f, category: v }))}>
                <SelectTrigger data-testid="listing-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CATEGORIES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin.status')}</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUSES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('admin.price')} (EUR) *</Label>
              <Input type="number" value={formData.price} onChange={(e) => setFormData(f => ({ ...f, price: e.target.value }))} required placeholder="95000" data-testid="listing-price-input" />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div><Label>{t('listings.area_label')} (м²)</Label><Input type="number" value={formData.area} onChange={(e) => setFormData(f => ({ ...f, area: e.target.value }))} placeholder="85" /></div>
            <div><Label>{t('listings.rooms_label')}</Label><Input type="number" value={formData.rooms} onChange={(e) => setFormData(f => ({ ...f, rooms: e.target.value }))} placeholder="3" /></div>
            <div><Label>{t('listings.floor_label')}</Label><Input value={formData.floor} onChange={(e) => setFormData(f => ({ ...f, floor: e.target.value }))} placeholder="4/8" /></div>
            <div><Label>{t('listings.year_label')}</Label><Input value={formData.year_built} onChange={(e) => setFormData(f => ({ ...f, year_built: e.target.value }))} placeholder="2024" /></div>
          </div>

          <div>
            <Label>{t('admin.description')} *</Label>
            <MultilingualDescriptionEditor
              values={{ bg: formData.description, el: formData.description_el, tr: formData.description_tr, en: formData.description_en }}
              onChange={(lang, text) => setFormData(f => ({ ...f, [lang === 'bg' ? 'description' : `description_${lang}`]: text }))}
              context={`${CATEGORIES[formData.category]}, ${formData.area}м², ${formData.rooms} стаи, ${formData.location}${formData.neighborhood ? `, кв. ${formData.neighborhood}` : ''}, ${formData.price} EUR`}
              placeholder={`Генерирай описание за ${CATEGORIES[formData.category]} за ${formData.listing_type === 'sale' ? 'продажба' : 'наем'}`}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.location')} *</Label>
              <Input value={formData.location} onChange={(e) => setFormData(f => ({ ...f, location: e.target.value }))} required placeholder="гр. София" data-testid="listing-location-input" />
            </div>
            <div>
              <Label>{t('listings.neighborhood_label')}</Label>
              <Input
                value={formData.neighborhood}
                onChange={(e) => setFormData(f => ({ ...f, neighborhood: e.target.value }))}
                placeholder="кв. Лозенец, кв. Изток, кв. Студентски"
                data-testid="listing-neighborhood-input"
              />
              <p className="text-xs text-muted-foreground mt-1">{t('listings.neighborhood_hint')}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div><Label>Latitude</Label><Input type="number" step="any" value={formData.latitude} onChange={(e) => setFormData(f => ({ ...f, latitude: e.target.value }))} placeholder="42.6777" /></div>
            <div><Label>Longitude</Label><Input type="number" step="any" value={formData.longitude} onChange={(e) => setFormData(f => ({ ...f, longitude: e.target.value }))} placeholder="23.3219" /></div>
          </div>

          <div>
            <Label>{t('listings.features')}</Label>
            <Input 
              defaultValue={formData.features.join(', ')}
              key={formData.features.join(',')}
              onBlur={(e) => setFormData(f => ({ ...f, features: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
              placeholder="Паркомясто, Асансьор, Балкон, Изба"
            />
            <p className="text-xs text-muted-foreground mt-1">{t('listings.features_hint')}</p>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div><Label>Контакт - име</Label><Input value={formData.contact_name} onChange={(e) => setFormData(f => ({ ...f, contact_name: e.target.value }))} placeholder="Иван Иванов" /></div>
            <div><Label>{t('listings.contact_phone')}</Label><Input value={formData.contact_phone} onChange={(e) => setFormData(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+359 888 123 456" /></div>
            <div><Label>{t('listings.contact_email')}</Label><Input value={formData.contact_email} onChange={(e) => setFormData(f => ({ ...f, contact_email: e.target.value }))} placeholder="ivan@email.com" /></div>
          </div>

          {/* Пазари / Markets */}
          <div className="border border-border rounded-xl p-4 bg-[#1B5E37]/5">
            <MarketsSelector
              value={formData.markets}
              onChange={(v) => setFormData(f => ({ ...f, markets: v }))}
            />
          </div>

          {/* Images */}
          <div>
            <Label className="mb-2 block">{t('admin.upload_images')} <span className="text-xs text-muted-foreground font-normal">({t('props.images_hint')})</span></Label>
            <div className="flex flex-wrap gap-3 mb-3">
              {formData.images.map((img, idx) => (
                <div
                  key={idx}
                  draggable
                  onDragStart={(e) => e.dataTransfer.setData('text/plain', idx)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const from = parseInt(e.dataTransfer.getData('text/plain'));
                    const imgs = [...formData.images];
                    const [moved] = imgs.splice(from, 1);
                    imgs.splice(idx, 0, moved);
                    setFormData(f => ({ ...f, images: imgs }));
                  }}
                  className="relative w-24 h-24 rounded-lg overflow-hidden border-2 group cursor-grab active:cursor-grabbing"
                  style={idx === 0 ? { borderColor: '#1B5E37' } : { borderColor: 'var(--border)' }}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && (
                    <span className="absolute bottom-0 left-0 right-0 bg-[#1B5E37] text-white text-[10px] text-center py-0.5 font-medium">COVER</span>
                  )}
                  <button type="button" onClick={() => removeImage(idx)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-xs">✕</button>
                  <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-60 transition-opacity">
                    <GripVertical className="w-4 h-4 text-white drop-shadow" />
                  </div>
                </div>
              ))}
              <label className="w-24 h-24 rounded-lg border-2 border-dashed border-border hover:border-[#1B5E37] flex flex-col items-center justify-center cursor-pointer transition-colors gap-1">
                <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageUpload} data-testid="listing-image-upload" />
                <Upload className="w-5 h-5 text-muted-foreground" />
                <span className="text-[10px] text-muted-foreground">Качи</span>
              </label>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>{t('admin.cancel')}</Button>
            <Button type="submit" disabled={saving} className="bg-[#1B5E37] hover:bg-[#154a2c]" data-testid="save-listing-btn">
              {saving ? t('admin.saving') : data ? t('admin.save') : t('listings.new_listing')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
